import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { COMPANY } from "../../lib/content";

const INFO = [
  { icon: Phone, label: "Call Us", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`, testid: "contact-phone" },
  { icon: Mail, label: "Email Us", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-email" },
];

export const Contact = () => {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#0C1812]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <span className="overline">Get in touch</span>
          <h2 className="font-serif-display text-[#FDFBF7] text-3xl md:text-5xl tracking-tight mt-4 leading-tight">
            Let's care for your property
          </h2>
          <p className="text-[#A3B1A8] mt-6 text-base md:text-lg leading-relaxed max-w-md">
            Whether you own a single home or a growing residential portfolio, our team is ready to manage it with
            the standard it deserves. Reach us the way that's easiest for you.
          </p>

          <a
            href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}
            data-testid="contact-call-cta"
            className="group mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-[#0A110D] font-semibold hover:bg-[#F0C742] transition-colors duration-300"
          >
            <Phone size={18} />
            Call {COMPANY.phone}
          </a>
        </motion.div>

        <div className="grid gap-5">
          {INFO.map((item, i) => (
            <motion.a
              key={item.label}
              href={item.href}
              data-testid={item.testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              className="block p-7 rounded-2xl bg-[#0F2119] border border-white/5 hover:border-[#D4AF37]/50 transition-colors duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5">
                <item.icon size={20} className="text-[#D4AF37]" />
              </div>
              <div className="overline text-[0.65rem] mb-2">{item.label}</div>
              <div className="text-[#FDFBF7] text-lg font-medium leading-snug">{item.value}</div>
            </motion.a>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            data-testid="contact-chat-note"
            className="p-7 rounded-2xl border border-[#D4AF37]/20 bg-[#0F2119]/60"
          >
            <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5">
              <MessageCircle size={20} className="text-[#D4AF37]" />
            </div>
            <div className="overline text-[0.65rem] mb-2">Live Chat</div>
            <div className="text-[#A3B1A8] text-sm leading-relaxed">
              Prefer to chat? Use the chat bubble in the corner of your screen and we'll respond right away.
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
